import React from 'react';
import { AppChrome } from '../alt/AppChrome';
import { ChatsScreen, WorkflowsScreen, PulsesScreen } from '../alt/PlatformPillar';
import { ConnectionsScreen } from '../alt/ConnectedAppsPillar';
import { SlackPanel } from '../SlackIntegrationGraph';
import { SkillShelf } from './SkillShelf';
import { AgentBuilderCard, ApprovalCard, ConnectionsCard } from '../aiagents/agentArtefacts';
import type { VerticalCrop } from './CreatorFrame';

/**
 * THE CREATOR PACK — the same product, composed for vertical video.
 *
 * A short, opinionated set rather than another full library. Someone cutting a
 * 30-second clip does not need twenty frames; they need the handful that carry
 * a story: what Giga is plugged into, it answering where the team already
 * talks, it answering in its own app, work running on a schedule, and the
 * library of methods behind all of it.
 *
 * Ordered as that story rather than by product area, so the pack cuts in
 * sequence straight down the page.
 *
 * Every plate is a STILL. Nothing here is interactive and nothing animates in
 * — an asset that has to be scrolled into view or clicked to look right is an
 * asset that exports wrong.
 */
export type CreatorAsset = {
  id: string;
  name: string;
  note: string;
  use: string;
  /** Natural width of the screen inside, for the plate's scale maths. */
  width: number;
  /**
   * Width the screen is rendered at ON the plate — the same in both ratios, so
   * switching shape changes the surrounding space and never the zoom. Only set
   * this when a screen needs to run wider than the default 508px.
   */
  renderWidth?: number;
  /**
   * How the 9:16 plate crops in. Vertical video is watched on a phone, so a
   * whole desktop screen scaled to a 486px column is unreadable — these plates
   * show a legible part instead of an illegible whole. `zoom` is the scale the
   * screen renders at inside the crop (≈1 means true size); `x` trims natural
   * px off the left when the point sits off-centre.
   */
  crop?: VerticalCrop;
  /**
   * A SINGLE OBJECT rather than a full app screen — a prompt, a settings card.
   *
   * These two facts always travel together, which is why they are one flag:
   * an object is rendered smaller than a screen (a 230px card blown up to fill
   * the artboard reads as a zoomed screenshot, not as a card sitting in a
   * frame), and it is centred vertically rather than ridden high, because it
   * does not fill the frame and the reserved caption band below a screen
   * becomes a void below an object.
   */
  object?: boolean;
  /**
   * Centre the plate vertically without the size reduction `object` brings.
   * For assets that already fill their own frame — a bled grid — but should
   * not ride high behind a caption band.
   */
  centered?: boolean;
  /** Optional narrower composition used only inside the 9:16 crop. */
  renderVertical?: () => React.ReactNode;
  render: () => React.ReactNode;
};
export const CREATOR_ASSETS: CreatorAsset[] = [{
  id: 'creator-connections',
  name: 'Integrations',
  note: 'Everything Giga is plugged into',
  use: 'The opening shot for "it works with what you already use". The densest screen in the pack — hold it a beat longer than the others, and prefer 16:9 where its own layout has room.',
  width: 1240,
  // Widest natural screen here, so it runs to the artboard's edges rather than
  // the shared default — below this its account labels stop reading.
  renderWidth: 512,
  // The connections table's meaning lives in its left half — the tool, its
  // name, its status. The right-hand columns are the first thing that becomes
  // unreadable on a phone anyway, so vertical drops them and keeps the rows at
  // two-thirds true size instead of showing all of it at a third.
  crop: {
    zoom: 0.66,
    x: 8
  },
  render: () => <AppChrome sidebar={false} data-id="element-3275" data-mp="3mb8lto91m9xr">
      <ConnectionsScreen data-id="element-3276" data-mp="3mafm378i4kp4" />
    </AppChrome>
}, {
  id: 'creator-slack',
  name: 'Slack thread',
  note: 'Someone asks in #sales, Giga answers',
  use: 'The most legible plate in the pack — a message column reads well narrow. Use it for the "no new app to learn" beat.',
  width: 900,
  // A thread is a column of text, so vertical is its natural shape: near true
  // size, cropped to the message column.
  crop: {
    zoom: 0.92
  },
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} settled data-id="element-3277" data-mp="1ko69o9mullz" />
}, {
  id: 'creator-chat',
  name: 'Giga Chat',
  note: 'An answer with its sources named',
  use: 'The product itself. Use it straight after the Slack plate to show the same answer has a home of its own, with the records it came from.',
  width: 900,
  crop: {
    zoom: 0.9
  },
  render: () => <AppChrome sidebar={false} data-id="element-3278" data-mp="3er6ftmval24a">
      <ChatsScreen data-id="element-3279" data-mp="3es7pbmwntgjw" />
    </AppChrome>
}, {
  id: 'creator-workflows',
  name: 'Workflows',
  note: 'Steps, and the record behind them',
  use: 'For the "it keeps working" beat. The strip beside the name is the part worth pausing on — 26 runs at 96%, its owner, and who can run it. That is the evidence; the steps are just the detail.',
  width: 900,
  // Padded the same way the landscape frames pad it — these two screens do
  // not carry their own inner gutter.
  crop: {
    zoom: 0.88
  },
  render: () => <AppChrome sidebar={false} data-id="element-3280" data-mp="uczs3fchgc8c">
      <div className="px-7 py-6" data-id="element-3281" data-mp="pfgf0iriu5hz">
        <WorkflowsScreen data-id="element-3282" data-mp="pg9bmqwrneme" />
      </div>
    </AppChrome>
}, {
  id: 'creator-pulses',
  name: 'Pulses',
  note: 'One running, three scheduled',
  use: 'The shortest plate to read in the pack — four rows, one of them mid-run with its step bar and a live Running chip, and a fired count on each. Good for a fast cut, or as the closing card under a voiceover.',
  width: 900,
  crop: {
    zoom: 0.88
  },
  render: () => <AppChrome sidebar={false} data-id="element-3283" data-mp="6q1huzeyi0iv">
      <div className="px-7 py-6" data-id="element-3284" data-mp="6mvpnbb16iix">
        <PulsesScreen data-id="element-3285" data-mp="1pm07vnn66v3" />
      </div>
    </AppChrome>
}, {
  id: 'creator-skills',
  name: 'Skills library',
  note: 'One in focus, the library receding behind',
  use: 'One skill held in focus with the rest falling away on both sides. Use it for the "your company\u2019s way of working, written down" beat — the falloff is what says there are dozens more.',
  // A NARROW CANVAS ON PURPOSE. Every other plate composes at 900px and is
  // reduced to ~0.6 to fit the artboard — fine for an app screen, fatal for
  // cards whose whole content is small type. Composing at 620 means the plate
  // renders this at ~0.9, so the card type survives the export.
  width: 620,
  renderWidth: 560,
  // No crop: the row already runs past its own edges and fades there, so the
  // plate has nothing left to cut — it just centres it.
  centered: true,
  // Three in view on a phone, five on a landscape plate. The centre card keeps
  // its size in both; only the number of receding neighbours changes.
  renderVertical: () => <SkillShelf spread={1} data-id="element-3286" data-mp="osaccdkmrh8l" />,
  render: () => <SkillShelf spread={2} data-id="element-3287" data-mp="or8zc3k20ko8" />
},
/*
 * ── THE AGENT SEQUENCE ────────────────────────────────────────────────────
 * Pulled from the AI Agents page, where they were built as idealised product
 * objects rather than screenshots. That is exactly why they belong here:
 * unlike a full app screen, each one is a SINGLE object about a SINGLE moment,
 * so it survives being watched on a phone without any cropping — and it gives
 * someone explaining the product a frame per step rather than one busy screen.
 *
 * Ordered as the sequence itself: describe the job, grant access, approve the
 * first action.
 */
/*
 * AN AGENT TEMPLATES plate opened this sequence. Dropped: it is the same
 * composition as the skills wall — a cropped grid of cards on a ruled board —
 * and two of those in one short pack read as a repeat rather than as two
 * ideas. The builder is the stronger opening anyway; it shows the job being
 * described rather than a menu of jobs.
 */
{
  id: 'creator-agent-builder',
  name: 'Agent builder',
  note: 'The job, described in plain words',
  use: 'The clearest single frame for "how do you make one?" — a brief in ordinary language, not a flowchart. Already phone-shaped, so it needs no cropping on a vertical cut.',
  // The brief sheet plus the settings panel it overlaps on the right, which is
  // absolutely positioned inside the card's own right padding.
  width: 380,
  object: true,
  render: () => <AgentBuilderCard data-id="element-3288" data-mp="1iyra07653i44" />
}, {
  id: 'creator-agent-connections',
  name: 'Agent access',
  note: 'Two granted, three waiting',
  use: 'The moment an agent is given the tools it needs. Small and single-purpose — good as an inset over footage of someone talking, rather than a full frame.',
  width: 230,
  object: true,
  render: () => <ConnectionsCard data-id="element-3289" data-mp="21qsebp1oo0kx" />
}, {
  id: 'creator-agent-approval',
  name: 'Approval prompt',
  note: 'Decline · approve · always allow',
  use: 'The most useful frame in the pack for an objection. Whenever someone asks what stops an agent doing something unattended, this is the answer — it shows the agent stopping to ask.',
  width: 300,
  object: true,
  render: () => <ApprovalCard data-id="element-3290" data-mp="cdyof6k0iit3" />
}];

/*
 * A SHARED AGENTS plate used to close this sequence. Dropped: the skills
 * library already makes the same point — one person writes something down and
 * the company inherits it — and it makes it with the author and the reuse
 * count visible, which the shared list did not. Two plates arguing one point
 * is one plate too many in a pack this short.
 */